// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Chrome fires "ResizeObserver loop completed with undelivered notifications" (and the older
// "ResizeObserver loop limit exceeded") whenever a ResizeObserver callback changes layout in a way
// the browser could not settle inside a single delivery. The spec defines this as a benign notice:
// the browser simply reschedules the remaining callbacks to the next frame and the user sees
// nothing. But it surfaces on window.onerror with no stack, so Pendo counts it as an "error click",
// and Datadog RUM and Sentry record it as an error.
//
// We suppress ONLY these two exact messages from those pipelines, so a genuine error is never
// hidden (AUT-6757). This is a targeted ignore of one known-benign browser notice, not a global
// window.onerror swallow. Our own ResizeObserver callbacks are additionally hardened to coalesce in
// a requestAnimationFrame and compare before they write (MapResizeObserver, OverflowTooltipText), so
// nothing in our bundle originates the loop; the surviving sessions trace to the client environment
// (a browser extension's own ResizeObserver, a knife-edge scrollbar oscillation).
const RESIZE_OBSERVER_LOOP_PATTERN =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

// Matches the benign ResizeObserver loop message. Used by the Datadog RUM `beforeSend` filter.
export const isResizeObserverLoopError = (message?: string | null): boolean =>
  typeof message === 'string' && RESIZE_OBSERVER_LOOP_PATTERN.test(message);

// Sentry's `ignoreErrors` accepts RegExps matched against the event message/exception value.
export const RESIZE_OBSERVER_LOOP_IGNORE = RESIZE_OBSERVER_LOOP_PATTERN;

// Capture-phase window 'error' guard. Installed before the Pendo agent (and any other error
// listener), so it runs first; when the benign ResizeObserver loop notice fires, it stops the event
// reaching the later listeners that would otherwise count it (Pendo's error-click metric above all).
// Every other error is left to propagate untouched. Idempotent and SSR-safe.
let guardInstalled = false;
export const installResizeObserverLoopGuard = (): void => {
  if (guardInstalled || typeof window === 'undefined') return;
  guardInstalled = true;
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (isResizeObserverLoopError(event.message)) {
        // Capture listeners run before bubble-phase listeners and the window.onerror property, so
        // this prevents Pendo and friends from ever seeing this one message.
        event.stopImmediatePropagation();
      }
    },
    true,
  );
};
